import fixWebmDuration from 'fix-webm-duration';
import { SAFE_RECORDING_PROFILE } from './media/recording-policy.js';
import { createOpfsRecordingSink } from './media/opfs-recording-sink.js';
import { createDirectGallerySink } from './media/direct-gallery-sink.js';
import {
  directGalleryController
} from './media/direct-gallery-recovery.js';
import { isDirectGallerySupported } from './media/direct-gallery-protocol.js';

export function getSupportedMimeType() {
  if (typeof MediaRecorder === "undefined") return "video/webm";

  // For CanvasStream composite recording (MediaRecorder from canvas.captureStream),
  // Chrome/WebView on Android ONLY supports WebM containers (VP8/VP9/H264-in-WebM).
  // MP4 container in MediaRecorder on Chrome Android is restricted to raw camera streams,
  // and throws NotSupportedError on canvas captureStream at recorder.start().
  const preferred = [
    "video/webm;codecs=vp8,opus",
    "video/webm;codecs=vp8",
    "video/webm;codecs=vp9",
    "video/webm;codecs=h264,opus",
    "video/webm;codecs=h264",
    "video/webm",
    "video/mp4;codecs=avc1,mp4a.40.2",
    "video/mp4;codecs=avc1",
    "video/mp4"
  ];

  for (const t of preferred) {
    if (MediaRecorder.isTypeSupported(t)) return t;
  }
  return "video/webm";
}

export function getRecordingExtension(mimeType = "") {
  return String(mimeType).toLowerCase().includes("mp4") ? "mp4" : "webm";
}

// getUserMedia video constraints for a given camera source. A non-empty
// deviceId (an external webcam / capture card picked by the user) is requested
// exactly and MUST NOT be paired with facingMode; an empty deviceId keeps the
// default back camera (facingMode: "environment"). Pure so it can be unit-tested.
export function buildVideoConstraints({ deviceId = "", preset = {}, fps = 30 } = {}) {
  const constraints = {
    width: { ideal: preset.w },
    height: { ideal: preset.h },
    frameRate: { ideal: fps || 30 }
  };
  if (deviceId) {
    constraints.deviceId = { exact: deviceId };
  } else {
    constraints.facingMode = "environment";
  }
  return constraints;
}

export function getRecordingLoadProfile(settings = {}) {
  const resolution = String(settings.resolution || "1080p");
  const fps = Number(settings.fps) || 30;

  if (resolution === "4k" || fps >= 60) {
    return {
      level: "extreme",
      label: "HEAVY LOAD",
      tone: "danger",
      message: "Use only for short clips on flagship phones; 1080p/30 is safer for full matches."
    };
  }
  if (resolution === "1440p") {
    return {
      level: "warm",
      label: "QUALITY MODE",
      tone: "warn",
      message: "Premium detail, but watch heat during long innings."
    };
  }
  if (resolution === "720p" && fps <= 25) {
    return {
      level: "safe",
      label: "LONG MATCH SAFE",
      tone: "good",
      message: "Best for lower heat, battery, and stable saved FPS."
    };
  }
  return {
    level: "balanced",
    label: "BROADCAST BALANCED",
    tone: "good",
    message: "Best default for full-match recording on modern phones."
  };
}

export function selectRecordingRuntimeProfile(settings = {}, environment = {}) {
  const selectedResolution = String(settings.resolution || "1080p");
  const selectedFps = Math.max(30, Number(settings.fps) || 30);
  const selectedBitrate = Number(settings.videoBitrate) || 12_000_000;
  const isAndroid = /Android/i.test(String(environment.userAgent || ""));
  const isAndroidWeb = !environment.isNative && isAndroid;
  const actualFps = Number(environment.actualFps) || 0;
  const highResolution = selectedResolution === "1440p" || selectedResolution === "4k";
  const measuredLowFps = highResolution && actualFps > 0 && actualFps < selectedFps * 0.8;
  const measuredCriticalFps = actualFps > 0 && actualFps < 24;

  // Android capture is the app's primary product path. Do not allow a legacy
  // OFF toggle to recreate a 2–6 FPS recording; exact-quality override remains
  // available on desktop where the hardware/encoder can be controlled.
  if (settings.stableRecordingProtection === false && !isAndroid) {
    return {
      resolution: selectedResolution,
      fps: selectedFps,
      videoBitrate: selectedBitrate,
      downshifted: false,
      reason: null
    };
  }
  if (measuredCriticalFps) {
    return {
      resolution: "720p",
      fps: 30,
      videoBitrate: 6_000_000,
      downshifted: true,
      reason: "measured-critical-fps"
    };
  }
  // Android web/WebView compositing runs on Canvas2D, which cannot sustain a
  // 1440p/4K broadcast overlay without heavy frame drops and thermal throttling.
  // Downshift high-resolution selections to a safe 1080p broadcast profile for
  // this recording only (the user's saved quality setting is left untouched).
  if (isAndroid && highResolution) {
    return {
      resolution: "1080p",
      fps: Math.min(selectedFps, 30),
      videoBitrate: 12_000_000,
      downshifted: true,
      reason: isAndroidWeb ? "android-web-high-resolution" : "android-high-resolution"
    };
  }
  if (measuredLowFps) {
    return {
      resolution: "1080p",
      fps: Math.min(selectedFps, 30),
      videoBitrate: 12_000_000,
      downshifted: true,
      reason: "measured-low-fps"
    };
  }
  if (isAndroid && selectedFps > 30) {
    return {
      resolution: selectedResolution,
      fps: 30,
      videoBitrate: Math.min(selectedBitrate, 18_000_000),
      downshifted: true,
      reason: isAndroidWeb ? "android-web-high-frame-rate" : "android-high-frame-rate"
    };
  }
  return {
    resolution: selectedResolution,
    fps: selectedFps,
    videoBitrate: selectedBitrate,
    downshifted: false,
    reason: null
  };
}

export function clampGradientStop(value) {
  return Math.max(0, Math.min(1, Number(value) || 0));
}

export function withCanvasState(ctx, draw) {
  ctx.save();
  try {
    return draw();
  } finally {
    ctx.restore();
  }
}

export async function createRecordingChunkSink({
  storageRoot = null,
  fileName = `recording-${Date.now()}.tmp`,
  maxMemoryBytes = 32 * 1024 * 1024,
  preferWorker = false,
  workerOptions = {}
} = {}) {
  let root = storageRoot;
  if (!root && globalThis.navigator?.storage?.getDirectory) {
    try { root = await globalThis.navigator.storage.getDirectory(); } catch (_) {}
  }

  if (preferWorker) {
    try {
      return await createOpfsRecordingSink({
        ...workerOptions,
        storageRoot: root,
        fileName
      });
    } catch (error) {
      console.warn('[RecUtils] SyncAccessHandle worker unavailable, using long-lived writable sink:', error);
    }
  }

  if (root) {
    try {
      const handle = await root.getFileHandle(fileName, { create: true });
      if (typeof handle.createWritable === 'function') {
        const writable = await handle.createWritable();
        let queue = Promise.resolve();
        return {
          persistent: true,
          write(chunk) {
            queue = queue.then(() => writable.write(chunk)).catch(err => {
              console.error('[RecUtils] chunk write failed:', err);
              // Don't propagate — keep queue alive for future chunks
            });
            return queue;
          },
          async finish() {
            try { await queue; } catch (_) {}
            try { await writable.close(); } catch (_) {}
            const blob = await handle.getFile();
            return { blob, cleanup: () => root.removeEntry(fileName).catch(() => {}) };
          }
        };
      }
    } catch (e) {
      console.warn('[RecUtils] OPFS sink init failed, falling back to memory:', e);
    }
  }

  const chunks = [];
  let bytes = 0;
  return {
    persistent: false,
    async write(chunk) {
      bytes += Number(chunk?.size) || 0;
      if (bytes > maxMemoryBytes) throw new Error("Recording memory safety limit reached");
      chunks.push(chunk);
    },
    async finish() {
      return { blob: new Blob(chunks), cleanup: async () => {} };
    }
  };
}

export function createQualificationMirrorSink({ primary, mirror } = {}) {
  if (!primary?.write || !primary?.finish || !mirror?.write || !mirror?.finish) {
    throw new TypeError('Primary and qualification mirror sinks are required');
  }
  let closed = false;
  return {
    persistent: true,
    kind: 'direct-gallery',
    qualificationMirror: true,
    async write(chunk) {
      if (closed) throw new Error('Qualification mirror sink is closed');
      await primary.write(chunk);
      await mirror.write(chunk);
    },
    async flush() {
      await primary.flush?.();
      await mirror.flush?.();
    },
    async finish() {
      if (closed) throw new Error('Qualification mirror sink is closed');
      closed = true;
      const published = await primary.finish();
      const mirrorResult = await mirror.finish();
      return {
        ...published,
        qualificationMirror: {
          kind: mirror.kind || 'opfs',
          bytes: mirrorResult?.blob?.size ?? null
        },
        cleanup: mirrorResult?.cleanup || (async () => {})
      };
    },
    abort(error, retainPartial = true) {
      closed = true;
      primary.abort?.(error, retainPartial);
      mirror.abort?.(error);
    },
    getMetrics() {
      return {
        primary: primary.getMetrics?.() || null,
        mirror: mirror.getMetrics?.() || null
      };
    }
  };
}

export async function createRecordingWriterSelection({
  settings = {},
  isNative = false,
  metadata = {},
  directController = directGalleryController,
  directSinkFactory = createDirectGallerySink,
  fallbackFactory = createRecordingChunkSink,
  fallbackOptions = {}
} = {}) {
  const createFallback = async fallbackReason => {
    const sink = await fallbackFactory(fallbackOptions);
    return {
      kind: sink?.kind || (sink?.persistent ? 'opfs-writable' : 'memory'),
      sink,
      qualificationMirror: false,
      fallbackReason
    };
  };

  if (settings.directGalleryWriter !== true || isNative !== true) {
    return createFallback(
      settings.directGalleryWriter === true ? 'Direct Gallery requires the native Android app' : null
    );
  }

  let session = null;
  try {
    const capability = await directController.getCapability();
    if (!isDirectGallerySupported({
      isNative,
      androidVersion: capability?.androidVersion,
      capability
    })) {
      return createFallback('Direct Gallery is unsupported on this device');
    }
    session = await directController.createSession({
      ...metadata,
      allowedOrigin: metadata.allowedOrigin || globalThis.location?.origin || 'https://localhost'
    });
    const primary = directSinkFactory({
      session,
      controller: directController
    });
    if (settings.directGalleryQualificationMirror === true) {
      const mirror = await fallbackFactory(fallbackOptions);
      return {
        kind: 'direct-gallery',
        sink: createQualificationMirrorSink({ primary, mirror }),
        qualificationMirror: true,
        fallbackReason: null
      };
    }
    return {
      kind: 'direct-gallery',
      sink: primary,
      qualificationMirror: false,
      fallbackReason: null
    };
  } catch (error) {
    if (session?.sessionId && typeof directController.deletePending === 'function') {
      await directController.deletePending(session.sessionId, true).catch(() => {});
    }
    const reason = error instanceof Error && error.message
      ? `Direct Gallery initialization failed: ${error.message}`
      : 'Direct Gallery initialization failed';
    return createFallback(reason);
  }
}

export async function finalizeRecordingSession({ sink, memoryChunks = [], mimeType = 'video/webm', durationMs = 0 } = {}) {
  let finalBlob = null;
  let cleanupFn = async () => {};
  let recoveredFromMemory = false;

  try {
    const result = await sink?.finish?.();
    if (result?.uri && Number(result.bytes) > 0) {
      return {
        uri: result.uri,
        bytes: Number(result.bytes),
        cleanup: result.cleanup || cleanupFn,
        qualificationMirror: result.qualificationMirror || null,
        recoveredFromMemory: false
      };
    }
    if (result?.blob instanceof Blob && result.blob.size > 0) {
      finalBlob = result.blob;
      cleanupFn = result.cleanup || cleanupFn;
    }
  } catch (_) {
    // The emergency chunk copy below is the final safety net when an OPFS
    // handle cannot be closed or read back after a long recording.
  }

  if (!finalBlob) {
    const chunks = Array.isArray(memoryChunks) ? memoryChunks.filter(chunk => chunk?.size > 0) : [];
    finalBlob = new Blob(chunks, { type: mimeType });
    recoveredFromMemory = true;
  }

  if (finalBlob.size <= 0) throw new Error('No recording data is available to save or recover.');

  // OPFS File objects and the in-memory chunk sink can lose the MediaRecorder
  // MIME type. Restore it before duration repair and download; otherwise some
  // Android players/converters treat a valid WebM payload as an unknown file
  // and the WebM duration fixer is skipped entirely.
  if (mimeType && finalBlob.type !== mimeType) {
    finalBlob = new Blob([finalBlob], { type: mimeType });
  }

  // Fix WebM duration metadata so the video is editable and shareable
  if (durationMs > 0 && finalBlob.type.includes('webm')) {
    try {
      finalBlob = await fixWebmDuration(finalBlob, durationMs, { logger: false });
    } catch (e) {
      console.warn('Failed to fix WebM duration metadata:', e);
    }
  }

  return { blob: finalBlob, cleanup: cleanupFn, recoveredFromMemory };
}

/**
 * Best-effort duration for a crash-recovered recording: first chunk to last
 * chunk. Slightly under-counts (start → first chunk ≈ chunk interval) which is
 * safer than over-counting for seek bars.
 */
export function estimateRecoveredDurationMs(manifest = {}) {
  const start = Number(manifest.createdAt);
  const end = Number(manifest.lastChunkAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return 0;
  return end - start;
}

/**
 * Writes duration metadata into a WebM blob (no-op for other types or when
 * durationMs is unknown). Recovered files need this or players show ∞/0:00.
 */
export async function applyWebmDuration(blob, durationMs) {
  if (!(blob instanceof Blob) || !(durationMs > 0) || !String(blob.type).includes('webm')) return blob;
  try {
    return await fixWebmDuration(blob, durationMs, { logger: false });
  } catch (error) {
    console.warn('WebM duration metadata fix failed:', error);
    return blob;
  }
}

export function createWorkerTimer(callback, intervalMs) {
  try {
    const code = `
      let timer = null;
      self.onmessage = function(e) {
        if (e.data === 'start') {
          timer = setInterval(() => self.postMessage('tick'), ${Math.max(1, intervalMs)});
        } else if (e.data === 'stop') {
          clearInterval(timer);
          timer = null;
        }
      };
    `;
    const blob = new Blob([code], { type: 'application/javascript' });
    const worker = new Worker(URL.createObjectURL(blob));
    worker.onmessage = () => callback(performance.now());
    worker.postMessage('start');
    return () => {
      try { worker.postMessage('stop'); worker.terminate(); } catch (_) {}
    };
  } catch (_) {
    const id = setInterval(() => callback(performance.now()), intervalMs);
    return () => clearInterval(id);
  }
}

export function createCompositeRecordingStream({
  canvas,
  mixedAudioStream = null,
  cameraStream = null,
  fps = 30,
  MediaStreamCtor = globalThis.MediaStream,
  requestFrameCallback = typeof globalThis.requestAnimationFrame === "function" ? globalThis.requestAnimationFrame : null,
  cancelFrameCallback = typeof globalThis.cancelAnimationFrame === "function" ? globalThis.cancelAnimationFrame : null,
  scheduleFrame = globalThis.setInterval?.bind(globalThis),
  cancelScheduledFrame = globalThis.clearInterval?.bind(globalThis),
  now = () => Date.now()
}) {
  if (!canvas || typeof canvas.captureStream !== "function") {
    throw new Error("This browser cannot record the broadcast overlay canvas.");
  }
  if (typeof MediaStreamCtor !== "function") {
    throw new Error("MediaStream is not available in this browser.");
  }

  const targetFps = Math.max(30, Number(fps) || 30);
  const frameInterval = 1000 / targetFps;

  // captureStream(0) = manual mode: we push frames via requestFrame()
  // This avoids the browser's timer-based auto-capture which drops to ~6fps
  // on Android WebView due to setInterval throttling.
  let canvasStream = canvas.captureStream(0);
  let videoTrack = canvasStream.getVideoTracks?.()?.[0];

  // Older WebViews expose canvas.captureStream() but not requestFrame(). A
  // zero-FPS stream on those engines never advances, so recreate it with the
  // selected FPS and let the browser drive capture automatically.
  if (videoTrack && typeof videoTrack.requestFrame !== "function") {
    try { videoTrack.stop?.(); } catch (_) {}
    canvasStream = canvas.captureStream(targetFps);
    videoTrack = canvasStream.getVideoTracks?.()?.[0];
  }

  if (videoTrack && typeof videoTrack.requestFrame === "function") {
    // Use requestAnimationFrame for smooth, consistent frame delivery. Keep a
    // low-frequency watchdog for Android WebViews that throttle rAF while the
    // browser UI is obscured; the watchdog only pushes when rAF has stalled, so
    // it does not double the requested frame rate during normal recording.
    let running = true;
    let lastFrameTime = 0;
    let lastRequestedAt = now();
    let animationFrameId = null;

    function pushCapturedFrame() {
      try { videoTrack.requestFrame(); } catch (_) {}
      lastRequestedAt = now();
    }

    function pumpFrame(timestamp) {
      if (!running) return;
      // Only push a frame if enough time has elapsed for the target FPS
      if (timestamp - lastFrameTime >= frameInterval * 0.9) {
        pushCapturedFrame();
        lastFrameTime = timestamp;
      }
      animationFrameId = requestFrameCallback?.(pumpFrame) ?? null;
    }
    if (requestFrameCallback) {
      animationFrameId = requestFrameCallback(pumpFrame);
    }

    // A target-rate timer is an independent watchdog for Android WebViews.
    // Some foreground WebViews throttle rAF to only a few callbacks per second
    // without setting document.hidden. A one-second watchdog produced 1–3 FPS
    // files in that state. Check every frame interval and push only when rAF
    // has not already delivered the current frame, avoiding a double pump.
    let fallbackTimer = null;
    if (scheduleFrame) {
      fallbackTimer = scheduleFrame(() => {
        if (now() - lastRequestedAt >= frameInterval * 0.8) {
          pushCapturedFrame();
        }
      }, frameInterval);
    }

    // Push first frame immediately
    pushCapturedFrame();

    videoTrack._stopFramePump = () => {
      running = false;
      if (animationFrameId != null && cancelFrameCallback) cancelFrameCallback(animationFrameId);
      if (fallbackTimer && cancelScheduledFrame) cancelScheduledFrame(fallbackTimer);
    };
  }
  if (!videoTrack) throw new Error("Broadcast canvas did not provide a video track.");

  const mixedAudioTrack = mixedAudioStream?.getAudioTracks?.()?.[0];
  const cameraAudioTrack = cameraStream?.getAudioTracks?.()?.[0];
  return new MediaStreamCtor([videoTrack, mixedAudioTrack || cameraAudioTrack].filter(Boolean));
}

export function buildMediaRecorderOptions(mimeType, settings = {}) {
  return {
    mimeType,
    videoBitsPerSecond: Number(settings.videoBitrate) || SAFE_RECORDING_PROFILE.videoBitrate,
    audioBitsPerSecond: Number(settings.audioBitrate) || SAFE_RECORDING_PROFILE.audioBitrate
  };
}

export function stopCompositeVideoTracks(stream) {
  stream?.getVideoTracks?.().forEach(track => {
    try { track._stopFramePump?.(); } catch (_) {}
    try { track.stop(); } catch (_) {}
  });
}

export function formatHighlightMetadata(match, ballSummary) {
  const title = `Cricket Highlight: ${match.teamA.name} vs ${match.teamB.name}`;
  const desc = `Ball: ${ballSummary}`;
  return { title, desc, matchId: match.id, date: new Date().toISOString() };
}
